import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import fr from 'facet-rest';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import {
  useManagedAccountPageContext,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Context/ManagedAccountPageContext';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { getSectionCardStyle } from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/styles';
import FAQButton from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/FAQButton';
import { useSnackbarContext } from 'Context/SnackbarContext';
import Divider from 'Components/UI/Divider';
import { dataAttrs } from 'Helpers/Testing';
import {
  FDBox, FDHeadlineText, FDLozenge, FDStack,
} from '@facet-wealth/design';
import useMobileLayout from 'Hooks/useMobileLayout';
import { MobileInterestCard } from './MobileInterestCard';
import ManagedAccountActivitySummary from './ManagedAccountActivitySummary';
import ManagedAccountPortfolio from './ManagedAccountPortfolio';

const useStyles = buildUseStyles('BalanceAndAllocationTabPanelLayout', (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  card: {
    ...getSectionCardStyle({ theme }),
  },
}));

async function fetchBalanceAndAllocationTabData(resource) {
  let activitySummary;
  let portfolio;

  // do nothing if the self link on the resource does not exist
  if (!resource?.link('self')) return {};

  const [balanceAndAllocation] = await resource.link('self').get();

  // fetch activity summary data
  try {
    const activitySummaryCollection = await balanceAndAllocation.relationship(fr.relationships.activitySummary.name)?.link('related').get();
    if (activitySummaryCollection) {
      [activitySummary] = activitySummaryCollection;
    }
  } catch (e) {
    // throw error for any status other than 404
    if (e.status !== 404) {
      throw e;
    }
  }

  // fetch portfolio data
  const portfolioCollection = await balanceAndAllocation.relationship(fr.relationships.portfolio.name)?.link('related').get();
  if (portfolioCollection) {
    [portfolio] = portfolioCollection;
  }

  return {
    balanceAndAllocation,
    activitySummary,
    portfolio,
  };
}

export function useManagedAccountBalanceAndAllocation(resource) {
  const { displayErrorMessage } = useSnackbarContext();

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(null);
  const [activitySummary, setActivitySummary] = useState(null);
  const [portfolio, setPortfolio] = useState(null);

  useEffect(() => {
    let ignore = false;

    (async function fetchData() {
      setIsLoading(true);
      setIsError(null);

      try {
        const {
          activitySummary: activitySummaryResource,
          portfolio: portfolioResource,
        } = await fetchBalanceAndAllocationTabData(resource);

        if (!ignore) {
          setActivitySummary(activitySummaryResource);
          setPortfolio(portfolioResource);
        }
      } catch (e) {
        setIsError(e || true);
        displayErrorMessage(e, (e.errors && e.errors[0]?.detail) || 'Unable to fetch account balance and allocations');
      }

      setIsLoading(false);
    }());

    return () => {
      ignore = true;
    };
  }, [resource]);

  return {
    isLoading,
    isError,
    activitySummary,
    portfolio,
  };
}

const BalanceAndAllocationTabPanelLayout = ({ className }) => {
  const classes = useStyles();
  const { currentTabResource, managedAccountPageResource } = useManagedAccountPageContext();
  const isMobile = useMobileLayout();
  const {
    isLoading,
    isError,
    activitySummary,
    portfolio,
  } = useManagedAccountBalanceAndAllocation(currentTabResource);

  if (isError) {
    return <Box><Subtitle value="Could not load the information. Please retry later." /></Box>;
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }

  const lozenges = managedAccountPageResource?.attr('lozenges').resources().list() || [];

  return (
    <Box className={classNames(classes.root, className)}>
      <FDStack spacing={2} direction="column" {...dataAttrs('managed-account-page-balance-section')}>
        <MobileInterestCard activitySummary={activitySummary} managedAccountPageResource={managedAccountPageResource} />
        <FDStack direction={'row'}>
          <FDHeadlineText value={'Balance'} />
          {!!managedAccountPageResource.relationship(fr.relationships.faq.name) && <>
            <FAQButton link={managedAccountPageResource.relationship(fr.relationships.faq.name).link('related')} />
          </>}
        </FDStack>
        <Box className={classes.card}>
          {activitySummary ? <ManagedAccountActivitySummary activitySummary={activitySummary} /> : null}
        </Box>
      </FDStack>
      <></>
      {!managedAccountPageResource?.isTruthy('showInterestRate')
        ? (
          <Box test="managed-account-page-allocation-section">
            <FDHeadlineText value={'Investment Mix'} />
            <br />
            <Box className={classes.card}>
              {managedAccountPageResource?.attr('allocationModelName') ? <>
                {isMobile ? (
                  <FDStack direction={'row'} justifyContent={'space-between'} spacing={2} wrap>
                    <Typography
                      color="primaryGray"
                      variant="h3"
                      weight="medium"
                      test="managed-account-allocation-model-name"
                    >
                      {managedAccountPageResource.attr('allocationModelName')}
                    </Typography>
                    {lozenges.length > 0 ? (
                      <FDBox>
                        <FDStack direction={'row'} justifyContent={'flex-end'} spacing={1}>
                          {lozenges.map(lozenge => (
                            <FDLozenge
                              key={lozenge.id()}
                              label={lozenge.attr('name')}
                              size={'sm'}
                              color={lozenge.attr('status')}
                            />
                          ))}
                        </FDStack>
                      </FDBox>
                    ) : null}
                  </FDStack>
                ) : null}
                {!isMobile ? (
                  <FDStack direction={'row'} justifyContent={'space-between'}>
                    <Typography color="primaryGray" variant="h3" weight="medium" test="managed-account-allocation-model-name">{managedAccountPageResource.attr('allocationModelName')}</Typography>
                    {lozenges.length > 0 ? (
                      <FDStack direction={'row'} justifyContent={'flex-end'} spacing={1} alignItems={'center'}>
                        {lozenges.map(lozenge => (
                          <FDLozenge key={lozenge.id()} label={lozenge.attr('name')} size={'lg'} color={lozenge.attr('status')} />
                        ))}
                      </FDStack>
                    ) : null}
                  </FDStack>
                ) : null}
                <Divider />
              </> : null}
              <ManagedAccountPortfolio portfolio={portfolio} />
            </Box>
          </Box>) : null}
    </Box>
  );
};

BalanceAndAllocationTabPanelLayout.defaultProps = {
  className: '',
};

BalanceAndAllocationTabPanelLayout.propTypes = {
  className: PropTypes.string,
};

export default BalanceAndAllocationTabPanelLayout;
